import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { MercadoVetKeys, type MercadoVetTab } from './mv_keys'
import { HeaderScope, MercadoVetScope } from './mv_scopes'
import type { SobreOsDados } from './mv_types'
import MvCnpjPresenter from './mv_presenter_cnpj'
import MvLinkedInPresenter from './mv_presenter_linkedin'
import MvWebPresenter from './mv_presenter_web'

const LOG = Logger.get('MV-Presenter')

export class MercadoVetPresenter extends CubePresenter<MainPresenter, MercadoVetScope> {
    constructor(app: MainPresenter) {
        super(app, new MercadoVetScope())
        this.headerBar = new Presenter(this, this.scope.header)
        this.cnpjPage = new MvCnpjPresenter(this)
        this.webPage = new MvWebPresenter(this)
        this.linkedInPage = new MvLinkedInPresenter(this)
    }

    // :: State

    readonly headerBar: Presenter<HeaderScope, MercadoVetPresenter>
    readonly cnpjPage: MvCnpjPresenter
    readonly webPage: MvWebPresenter
    readonly linkedInPage: MvLinkedInPresenter

    // :: Lifecycle

    override release() {
        this.app.releaseToolbar(this.headerBar)
        this.cnpjPage.release()
        this.webPage.release()
        this.linkedInPage.release()
        this.headerBar.release()
        super.release()
        LOG.debug('Finalized')
    }

    override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        if (!this.app.scope.mercadoVetEnabled) {
            await this.app.flipToDefault()
            return false
        }

        const keys = new MercadoVetKeys(this.app, intent)

        this.app.scope.toolbar = this.headerBar.scope
        keys.parentSlot(this.scope)
        keys.mvPresenter = this

        if (initialization) {
            this.scope.onTabChange = this.action(this.__handleTabChange)
            await this.cnpjPage.initialize()
            await this.webPage.initialize()
            this.linkedInPage.initialize()
        }

        await this.__showTab(keys.tab)
        await this.cnpjPage.filterPane.catalog.applyParameters(keys, initialization)
        await this.webPage.filterPane.catalog.applyParameters(keys, initialization)

        if (keys.last) {
            keys.dialogSlot(null)
        }

        return keys.allow
    }

    override publishParameters(intent: FlipIntent): void {
        const keys = new MercadoVetKeys(this.app, intent)
        keys.tab = this.scope.tab
    }

    // :: Callbacks das abas

    onSobreOsDados(sobre: SobreOsDados) {
        this.scope.indice = sobre.indice
        this.scope.atualizadoEm = sobre.atualizadoEm ? this.app.formatDate(new Date(sobre.atualizadoEm)) : ''
        this.scope.dataDeCorte = sobre.dataCorte ? this.app.formatDate(new Date(sobre.dataCorte)) : ''
    }

    onCnpjFilterChanged() {
        if (this.scope.tab === 'linkedin') {
            this.linkedInPage.activate(this.cnpjPage.buildFilter()).catch(LOG.caught)
        } else {
            this.linkedInPage.invalidate()
        }
    }

    // :: Internals

    private async __handleTabChange(tab: MercadoVetTab) {
        await this.__showTab(tab)
        this.updateHistory()
    }

    private async __showTab(tab: MercadoVetTab) {
        this.scope.tab = tab
        this.headerBar.scope.busca = this.scope[tab].busca
        this.headerBar.scope.buscaAba = tab
        switch (tab) {
            case 'web':
                await this.webPage.activate()
                break
            case 'linkedin':
                this.cnpjPage.activate().catch(LOG.caught)
                await this.linkedInPage.activate(this.cnpjPage.buildFilter())
                break
            default:
                await this.cnpjPage.activate()
        }
    }
}

export default MercadoVetPresenter
